/**
 * The HeaderButton and HeaderLinkButton are reusable components for rendering
 * the current app listings call to action with some default styling and behaviour
 * to the buttons and links used in the header of the app.
 */

import {Button, LinkButton, VisuallyHidden} from '@primer/react'
import type {PlanInfo} from '../../types'
import checkCanInstall from '../../utilities/check-can-install'
import {useId, type ComponentProps} from 'react'
import styles from '../../marketplace.module.css'

export type HeaderButtonBaseProps = {
  planInfo: PlanInfo
}

type HeaderButtonProps = HeaderButtonBaseProps & ComponentProps<typeof Button>

function useHeaderButtonState(planInfo: PlanInfo, props: {disabled?: boolean}) {
  const descriptionId = useId()
  const {canInstall, reason} = checkCanInstall(planInfo)
  const disabled = Boolean(!canInstall || props.disabled)

  return {
    disabled,
    descriptionId: !canInstall && reason ? descriptionId : undefined,
    reason,
  } as const
}

export function HeaderButton({planInfo, children, className, ...buttonProps}: HeaderButtonProps) {
  const {disabled, descriptionId, reason} = useHeaderButtonState(planInfo, buttonProps)
  const ariaDescribedBy = [buttonProps['aria-describedby'], descriptionId].filter(Boolean).join(' ') || undefined

  return (
    <>
      <Button
        as="button"
        variant="primary"
        className={`width-full ${styles.ResponsiveHeaderControl} ${className ?? ''}`}
        size="large"
        {...buttonProps}
        aria-disabled={disabled}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
      >
        {children}
      </Button>
      {descriptionId && <VisuallyHidden id={descriptionId}>{reason}</VisuallyHidden>}
    </>
  )
}

type HeaderLinkButtonProps = HeaderButtonBaseProps & ComponentProps<typeof LinkButton>

export function HeaderLinkButton({planInfo, children, className, ...linkButtonProps}: HeaderLinkButtonProps) {
  const {disabled, descriptionId, reason} = useHeaderButtonState(planInfo, linkButtonProps)
  const ariaDescribedBy = [linkButtonProps['aria-describedby'], descriptionId].filter(Boolean).join(' ') || undefined

  return (
    <>
      <LinkButton
        as="a"
        variant="primary"
        className={`width-full ${styles.ResponsiveHeaderControl} ${className ?? ''}`}
        size="large"
        data-testid="header-link-button"
        {...linkButtonProps}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
      >
        {children}
      </LinkButton>
      {descriptionId && <VisuallyHidden id={descriptionId}>{reason}</VisuallyHidden>}
    </>
  )
}
